// ========================================
// CONTEXTO DE AUTENTICAÇÃO - COM SEGURANÇA
// frontend/src/contexts/AuthContext.jsx
// ========================================

import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { usuarioAPI } from '../services/api';
import { api, refreshSession } from '../config/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [usuario, setUsuario] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const refreshTimerRef = useRef(null);

  // ========================================
  // AUTO-REFRESH PROATIVO A CADA 14 MINUTOS
  // O access_token expira em 15min — renova antes para nunca expirar em uso
  // ========================================
  useEffect(() => {
    if (authenticated) {
      // Inicia o timer ao autenticar
      refreshTimerRef.current = setInterval(async () => {
        const ok = await refreshSession();
        if (!ok) {
          clearInterval(refreshTimerRef.current);
          refreshTimerRef.current = null;
          setUsuario(null);
          setAuthenticated(false);
        }
      }, 14 * 60 * 1000); // 14 minutos
    } else {
      // Para o timer ao deslogar
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    }

    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [authenticated]);

  // ========================================
  // VERIFICAÇÃO DE MEIA-NOITE — FORÇAR RE-LOGIN DIÁRIO
  // Verifica a cada 30s se passou da meia-noite desde o login.
  // Se sim, faz logout e redireciona para a tela de login.
  // ========================================
  useEffect(() => {
    if (!authenticated) return;

    // Guardar a data (dia) em que a sessão foi autenticada
    const diaLogin = new Date().toDateString();

    const midnightCheck = setInterval(() => {
      const diaAtual = new Date().toDateString();
      if (diaAtual !== diaLogin) {
        console.warn('🕛 Meia-noite detectada — forçando re-login');
        clearInterval(midnightCheck);
        // Limpar sessão
        try { api.post('/auth/logout', {}); } catch { /* ignore */ }
        limparLegadoLocalStorage();
        setUsuario(null);
        setAuthenticated(false);
      }
    }, 30 * 1000); // Verifica a cada 30 segundos

    return () => clearInterval(midnightCheck);
  }, [authenticated]);

  // Verificar autenticação ao carregar
  useEffect(() => {
    checkAuth();
  }, []);

  const limparLegadoLocalStorage = () => {
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('usuario');
    } catch {
      // ignore
    }
  };

  // ========================================
  // BUSCAR DADOS COMPLETOS DO USUÁRIO VIA BACKEND
  // Fallback quando o backend não retorna todos os campos
  // ========================================
  const buscarDadosCompletosUsuario = async (usuarioId, usuarioUsername) => {
    try {
      let data = null;
      
      if (usuarioId) {
        data = await usuarioAPI.buscarPorId(usuarioId);
      } else if (usuarioUsername) {
        data = await usuarioAPI.buscarPorUsername(usuarioUsername);
      }
      
      if (!data) {
        console.warn('⚠️ Usuário não encontrado no backend');
        return null;
      }
      
      return data;
    } catch (e) {
      console.error('❌ Erro ao buscar no backend:', e);
      return null;
    }
  };

  // ========================================
  // NORMALIZAR DADOS DO USUÁRIO
  // Garante que os campos tenham nomes consistentes
  // ========================================
  const normalizarUsuario = async (userData) => {
    if (!userData) return null;

    let dadosCompletos = { ...userData };

    // Se não tiver departamento, buscar do backend
    if (!userData.departamento || userData.departamento === '') {
      const dadosBackend = await buscarDadosCompletosUsuario(
        userData.id, 
        userData.username || userData.username_ldap
      );
      
      if (dadosBackend) {
        dadosCompletos = { ...userData, ...dadosBackend };
      }
    }

    const usuarioNormalizado = {
      ...dadosCompletos,
      // Garantir que 'nome' existe (pode vir como nome_completo do banco)
      nome: dadosCompletos.nome || dadosCompletos.nome_completo || 'Usuário',
      // Garantir que departamento e cargo existem
      departamento: dadosCompletos.departamento || '',
      cargo: dadosCompletos.cargo || '',
      // Garantir permissões
      permissoes: {
        pode_criar_pedido: dadosCompletos.permissoes?.pode_criar_pedido ?? dadosCompletos.pode_criar_pedido ?? false,
        pode_ver_tabela_admin: dadosCompletos.permissoes?.pode_ver_tabela_admin ?? dadosCompletos.pode_ver_tabela_admin ?? false,
        pode_ver_pedidos: dadosCompletos.permissoes?.pode_ver_pedidos ?? dadosCompletos.pode_ver_pedidos ?? false,
        pode_ver_dashboard: dadosCompletos.permissoes?.pode_ver_dashboard ?? dadosCompletos.pode_ver_dashboard ?? false,
        pode_aprovar_usuarios: dadosCompletos.permissoes?.pode_aprovar_usuarios ?? dadosCompletos.pode_aprovar_usuarios ?? false,
        pode_gerenciar_tabelas: dadosCompletos.permissoes?.pode_gerenciar_tabelas ?? dadosCompletos.pode_gerenciar_tabelas ?? false,
        pode_aprovar_clientes: dadosCompletos.permissoes?.pode_aprovar_clientes ?? dadosCompletos.pode_aprovar_clientes ?? false,
        pode_ver_campanhas: dadosCompletos.permissoes?.pode_ver_campanhas ?? dadosCompletos.pode_ver_campanhas ?? false,
        pode_vender_produtos_especiais: dadosCompletos.permissoes?.pode_vender_produtos_especiais ?? dadosCompletos.pode_vender_produtos_especiais ?? false,
        pode_vender_abaixo_minimo: dadosCompletos.permissoes?.pode_vender_abaixo_minimo ?? dadosCompletos.pode_vender_abaixo_minimo ?? false,
        pode_alterar_responsavel: dadosCompletos.permissoes?.pode_alterar_responsavel ?? dadosCompletos.pode_alterar_responsavel ?? false,
        pode_trocar_cliente_pedido: dadosCompletos.permissoes?.pode_trocar_cliente_pedido ?? dadosCompletos.pode_trocar_cliente_pedido ?? false,
      },
      // Fluxo automático de aprovação (direto no objeto, não dentro de permissoes)
      fluxo_automatico: dadosCompletos.fluxo_automatico || false,
      // Outros campos importantes
      regiao_acesso: dadosCompletos.regiao_acesso || 'Todos',
      codigo_id: dadosCompletos.codigo_id || dadosCompletos.id || '',
      precisa_trocar_senha: dadosCompletos.precisa_trocar_senha || false,
    };

    return usuarioNormalizado;
  };

  const checkAuth = async () => {
    try {
      const response = await api.get('/auth/me');
      if (!response.success) {
        throw new Error(response.message || 'Não autenticado');
      }

      const usuarioData = response.usuario;
      const usuarioNormalizado = await normalizarUsuario(usuarioData);
      setUsuario(usuarioNormalizado);
      setAuthenticated(true);

    } catch (error) {
      // Se falhar, considerar deslogado
      console.warn('⚠️ Sessão não autenticada:', error?.message || error);
      limparLegadoLocalStorage();
      setUsuario(null);
      setAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (usuarioData) => {
    const usuarioNormalizado = await normalizarUsuario(usuarioData);
    setUsuario(usuarioNormalizado);
    setAuthenticated(true);
  };

  const logout = async () => {

    // Limpar cookies no backend (se possível)
    try {
      await api.post('/auth/logout', {});
    } catch {
      // ignore
    }

    limparLegadoLocalStorage();
    setUsuario(null);
    setAuthenticated(false);
  }; 

  // Verificar se usuário tem permissão específica
  const temPermissao = (permissao) => {
    if (!usuario || !usuario.permissoes) return false;
    return usuario.permissoes[permissao] === true;
  };

  // Verificar se usuário tem alguma das permissões
  const temAlgumaPermissao = (...permissoes) => {
    return permissoes.some(p => temPermissao(p));
  };

  // Verificar se usuário tem todas as permissões
  const temTodasPermissoes = (...permissoes) => {
    return permissoes.every(p => temPermissao(p));
  };

  // Verificar se pode ver determinada região
  const podeVerRegiao = (estado) => {
    if (!usuario) return false;
    if (usuario.regiao_acesso === 'Todos') return true;
    return usuario.regiao_acesso === estado;
  };

  // Verificar se precisa trocar senha (primeiro acesso)
  const precisaTrocarSenha = () => {
    if (!usuario) return false;
    return usuario.precisa_trocar_senha === true;
  };

  // ========================================
  // VERIFICAR SE É ADMIN OU DIRETORIA
  // Verifica SEMPRE pelo campo DEPARTAMENTO
  // ========================================
  const isAdminOuDiretoria = () => {
    if (!usuario) return false;
    
    const departamento = (usuario.departamento || '').toLowerCase().trim();
    
    // Verifica APENAS por departamento - somente Admin e Diretoria (não Administrativo)
    // Administrativo pode ver dados de todos os vendedores, mas não a visão financeira
    const resultado = departamento === 'admin' || 
                      departamento === 'diretoria' || 
                      departamento === 'administrador';
    
    return resultado;
  };

  const value = {
    usuario,
    loading,
    authenticated,
    login,
    logout,
    temPermissao,
    temAlgumaPermissao,
    temTodasPermissoes,
    podeVerRegiao,
    precisaTrocarSenha,
    checkAuth,
    isAdminOuDiretoria // Nova função exportada
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Hook personalizado para usar o contexto
export const useAuth = () => {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  
  return context;
};

export default AuthContext;